import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { useMentorSession } from '../context/TrackerContext';
import {
  getJuniorMentorOverview, getSeniorLeads, assignLeadToMentor, mentorReassignStudent,
} from '../lib/api';
import type { JuniorMentor, JuniorStudent, UnassignedLead } from '../lib/api';

// ==================== TEAM OVERVIEW SUB-COMPONENTS ===========

const PHASE_COLORS: Record<string, { bg: string; color: string }> = {
  'First 5 Days': { bg: '#fef3c7', color: '#92400e' },
  'Mid Course':   { bg: '#ede9fe', color: '#4c1d95' },
  'Last 10 Days': { bg: '#dcfce7', color: '#14532d' },
};

const AttBadge: React.FC<{ val: string }> = ({ val }) => {
  if (!val) return <span className="text-xs text-gray-300">—</span>;
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${val === 'Present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
      {val}
    </span>
  );
};

const StatusBadge: React.FC<{ val: string }> = ({ val }) => {
  const map: Record<string, string> = {
    'Completed': 'bg-green-100 text-green-700', 'Task Given': 'bg-amber-100 text-amber-700',
    'Not Completed': 'bg-red-100 text-red-600',
  };
  if (!val) return <span className="text-xs text-gray-300">—</span>;
  return <span className={`text-xs px-2 py-0.5 rounded font-semibold ${map[val] ?? 'bg-gray-100 text-gray-600'}`}>{val}</span>;
};

const StudentRow: React.FC<{ s: JuniorStudent; idx: number; onReassign: () => void }> = ({ s, idx, onReassign }) => {
  const phase = s.phase || 'First 5 Days';
  const pc = PHASE_COLORS[phase] ?? PHASE_COLORS['First 5 Days'];
  const daysSince = s.lastClass?.date
    ? Math.floor((Date.now() - new Date(s.lastClass.date).getTime()) / 86400000) : null;
  return (
    <tr className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
      <td className="px-3 py-2.5">
        <div className="font-semibold text-sm text-gray-900">{s.name}</div>
        <div className="text-xs text-gray-400 font-mono">{s.studentId}</div>
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{s.course}</td>
      <td className="px-3 py-2.5">
        <span className="text-xs px-2 py-0.5 rounded font-semibold" style={{ background: pc.bg, color: pc.color }}>
          {phase === 'First 5 Days' ? 'Day 1-5' : phase === 'Mid Course' ? 'Mid' : 'Last 10'}
        </span>
      </td>
      <td className="px-3 py-2.5 text-center"><span className="text-sm font-bold text-gray-700">{s.totalClasses}</span></td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        {s.lastClass ? (
          <div>
            <div className="text-xs font-medium text-gray-700">{s.lastClass.date}</div>
            {daysSince !== null && (
              <div className={`text-xs ${daysSince > 3 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                {daysSince === 0 ? 'Today' : `${daysSince}d ago`}
              </div>
            )}
          </div>
        ) : <span className="text-xs text-gray-300">No class yet</span>}
      </td>
      <td className="px-3 py-2.5"><AttBadge val={s.lastClass?.attendance ?? ''} /></td>
      <td className="px-3 py-2.5"><StatusBadge val={s.lastClass?.completionStatus ?? ''} /></td>
      <td className="px-3 py-2.5 text-xs text-gray-500 max-w-[120px] truncate" title={s.lastClass?.topic}>
        {s.lastClass?.topic || '—'}
      </td>
      <td className="px-3 py-2.5">
        <button onClick={onReassign}
          className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-100 whitespace-nowrap">
          Reassign
        </button>
      </td>
    </tr>
  );
};

const JuniorCard: React.FC<{ jm: JuniorMentor; onReassignStudent: (s: JuniorStudent, fromMentorId: string) => void }> = ({ jm, onReassignStudent }) => {
  const [open, setOpen] = useState(true);
  const presentToday = jm.students.filter(s => {
    if (!s.lastClass) return false;
    return Math.floor((Date.now() - new Date(s.lastClass.date).getTime()) / 86400000) === 0 && s.lastClass.attendance === 'Present';
  }).length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
            style={{ background: '#2b2240', color: '#F0B429' }}>
            {jm.mentorName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div className="text-left">
            <p className="font-bold text-gray-900">{jm.mentorName}</p>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-xs text-gray-500 font-mono">{jm.mentorId}</span>
              {jm.phone && <span className="text-xs text-gray-500">{jm.phone}</span>}
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#f0edf7', color: '#2b2240' }}>
                {jm.students.length} students
              </span>
              {presentToday > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  {presentToday} present today
                </span>
              )}
            </div>
          </div>
        </div>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      {open && (
        <div className="border-t border-gray-100 overflow-x-auto">
          {jm.students.length === 0 ? (
            <p className="px-5 py-4 text-xs text-gray-400">No active students assigned.</p>
          ) : (
            <table className="w-full text-xs border-collapse min-w-[750px]">
              <thead style={{ background: '#f8f6ff' }}>
                <tr>
                  {['Student', 'Course', 'Phase', 'Classes', 'Last Class', 'Attendance', 'Status', 'Last Topic', ''].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jm.students.map((s, i) => (
                  <StudentRow key={s.studentId} s={s} idx={i} onReassign={() => onReassignStudent(s, jm.mentorId)} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== REASSIGN STUDENT MODAL =================

const ReassignStudentModal: React.FC<{
  student: JuniorStudent;
  fromMentorId: string;
  juniors: JuniorMentor[];
  seniorMentorId: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ student, fromMentorId, juniors, seniorMentorId, onClose, onDone }) => {
  const [newMentorId, setNewMentorId] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMentorId) { toast.error('Select a mentor'); return; }
    if (newMentorId === fromMentorId) { toast.error('Same mentor selected'); return; }
    setSaving(true);
    try {
      await mentorReassignStudent(seniorMentorId, student.studentId, newMentorId);
      toast.success(`${student.name} reassigned`);
      onDone();
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10">
        <h3 className="text-base font-bold mb-1" style={{ color: '#2b2240' }}>Reassign Student</h3>
        <p className="text-xs text-gray-500 mb-4">Move <span className="font-semibold text-gray-700">{student.name}</span> to a different mentor</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">New Mentor</label>
            <select value={newMentorId} onChange={e => setNewMentorId(e.target.value)} required
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 bg-white">
              <option value="">— Select mentor —</option>
              {juniors.filter(j => j.mentorId !== fromMentorId).map(j => (
                <option key={j.mentorId} value={j.mentorId}>{j.mentorName}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
              {saving ? 'Saving…' : 'Reassign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== ASSIGN LEAD MODAL ======================

const AssignLeadModal: React.FC<{
  lead: UnassignedLead;
  juniors: JuniorMentor[];
  seniorMentorId: string;
  onClose: () => void;
  onDone: (id: string) => void;
}> = ({ lead, juniors, seniorMentorId, onClose, onDone }) => {
  const [juniorMentorId, setJuniorMentorId] = useState('');
  // Preselect by the student's mode: a website sign-up is 'group' + an online /
  // offline category; older records say 'online' / 'offline' or 'one-to-one' (online)
  const [sessionType, setSessionType] = useState<'group' | 'one-to-one'>(() => {
    const type = (lead.sessionType || '').toLowerCase();
    const online = lead.sessionCategory ? lead.sessionCategory === 'online' : type === 'online' || (type !== 'offline' && type.includes('one'));
    return online ? 'one-to-one' : 'group';
  });
  const [courseFee, setCourseFee] = useState(String(lead.courseFee || (sessionType === 'one-to-one' ? 15000 : 20000)));
  const [regPaid, setRegPaid] = useState(String(lead.regPaid || 1000));
  const [sendWelcome, setSendWelcome] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!juniorMentorId) { toast.error('Select a mentor'); return; }
    setSaving(true);
    try {
      await assignLeadToMentor(seniorMentorId, lead._id, juniorMentorId, sessionType, Number(courseFee) || 0, Number(regPaid) || 1000, sendWelcome);
      toast.success(`${lead.name} assigned! ${sendWelcome && lead.email ? 'Welcome email sent.' : ''}`);
      onDone(lead._id);
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold" style={{ color: '#2b2240' }}>Assign Lead to Mentor</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 mb-4 space-y-1">
          {[['Name', lead.name], ['Phone', lead.phone || '—'], ['Email', lead.email || '—'], ['Course', lead.course || '—']].map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs">
              <span className="text-gray-500">{k}</span>
              <span className="font-medium text-gray-700">{v}</span>
            </div>
          ))}
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Session Type</label>
            <div className="grid grid-cols-2 gap-2">
              {(['group', 'one-to-one'] as const).map(t => (
                <button key={t} type="button" onClick={() => { setSessionType(t); setCourseFee(t === 'one-to-one' ? '15000' : '20000'); }}
                  className="py-2 rounded-lg text-xs font-bold border-2 transition-all"
                  style={sessionType === t
                    ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                    : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}>
                  {t === 'group' ? 'Group (offline) — ₹20,000' : 'One-to-One (online) — ₹15,000'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Assign to Mentor *</label>
            <select value={juniorMentorId} onChange={e => setJuniorMentorId(e.target.value)} required
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 bg-white">
              <option value="">— Select mentor —</option>
              {juniors.map(j => <option key={j.mentorId} value={j.mentorId}>{j.mentorName}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Course Fee (₹)</label>
              <input type="number" value={courseFee} onChange={e => setCourseFee(e.target.value)} min="0"
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Reg Paid (₹)</label>
              <input type="number" value={regPaid} onChange={e => setRegPaid(e.target.value)} min="0"
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400" />
            </div>
          </div>
          {lead.email && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={sendWelcome} onChange={e => setSendWelcome(e.target.checked)} className="w-4 h-4 rounded" />
              <span className="text-xs text-gray-600">Send welcome email to {lead.email}</span>
            </label>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
              {saving ? 'Assigning…' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== UNASSIGNED LEADS TAB ===================

const UnassignedLeadsTab: React.FC<{ mentorId: string; juniors: JuniorMentor[] }> = ({ mentorId, juniors }) => {
  const [leads, setLeads] = useState<UnassignedLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assignLead, setAssignLead] = useState<UnassignedLead | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSeniorLeads(mentorId);
      setLeads(res.leads ?? []);
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [mentorId]);

  useEffect(() => { load(); }, [load]);

  const visible = leads.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return l.name.toLowerCase().includes(q) || (l.phone || '').includes(q) || (l.email || '').toLowerCase().includes(q) || (l.course || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
            className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400" />
        </div>
        <span className="px-3 py-1.5 rounded-full text-xs font-bold text-white" style={{ background: '#FF6535' }}>
          {leads.length} pending
        </span>
        <button onClick={load} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:bg-gray-50">
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="text-4xl mb-3">🎉</div>
          <p className="text-gray-600 font-semibold">No unassigned leads</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[600px]">
              <thead>
                <tr style={{ background: '#2b2240' }}>
                  {['#', 'Name', 'Course', 'Session', 'Phone', 'Reg Paid', 'Date', 'Assign'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-white whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((l, idx) => (
                  <tr key={l._id} className={`border-b border-gray-100 hover:bg-gray-50 ${idx % 2 === 0 ? '' : 'bg-gray-50/40'}`}>
                    <td className="px-3 py-3 text-xs text-gray-400">{idx + 1}</td>
                    <td className="px-3 py-3">
                      <div className="font-semibold text-gray-800">{l.name}</div>
                      {l.email && <div className="text-xs text-gray-400">{l.email}</div>}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 max-w-[130px] truncate">{l.course || '—'}</td>
                    <td className="px-3 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={(l.sessionType || '').toLowerCase().includes('one')
                          ? { background: '#ede9fe', color: '#5b21b6' }
                          : { background: '#e0f2fe', color: '#075985' }}>
                        {(l.sessionType || '').toLowerCase().includes('one') ? '1:1' : 'Group'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600">{l.phone || '—'}</td>
                    <td className="px-3 py-3 text-xs font-medium text-gray-700">₹{(l.regPaid || 0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-xs text-gray-500">{l.enrollDate || '—'}</td>
                    <td className="px-3 py-3">
                      <button onClick={() => setAssignLead(l)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold text-white whitespace-nowrap hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
                        Assign ▾
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {assignLead && (
        <AssignLeadModal
          lead={assignLead}
          juniors={juniors}
          seniorMentorId={mentorId}
          onClose={() => setAssignLead(null)}
          onDone={id => { setAssignLead(null); setLeads(prev => prev.filter(l => l._id !== id)); }}
        />
      )}
    </div>
  );
};

// ==================== MAIN PAGE ==============================

type Tab = 'team' | 'leads';

const SeniorTeamOverview: React.FC = () => {
  const { mentorId, mentorName } = useMentorSession();
  const [tab, setTab] = useState<Tab>('team');
  const [juniors, setJuniors] = useState<JuniorMentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [reassignTarget, setReassignTarget] = useState<{ s: JuniorStudent; fromMentorId: string } | null>(null);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getJuniorMentorOverview(mentorId);
      setJuniors(res.juniors);
    } catch (err: any) { toast.error('Failed to load team: ' + err.message); }
    finally { setLoading(false); }
  }, [mentorId]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const totalStudents = juniors.reduce((s, j) => s + j.students.length, 0);
  const missedClasses = juniors.flatMap(j => j.students).filter(s => {
    if (!s.lastClass) return false;
    return Math.floor((Date.now() - new Date(s.lastClass.date).getTime()) / 86400000) > 3;
  }).length;

  return (
    <TrackerLayout>
      <div className="flex flex-col h-full" style={{ minHeight: 'calc(100vh - 65px)' }}>
        {/* Header */}
        <div className="flex-shrink-0 flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#2b2240' }}>Senior Dashboard</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {mentorName} &nbsp;·&nbsp; {juniors.length} junior mentor{juniors.length !== 1 ? 's' : ''} &nbsp;·&nbsp; {totalStudents} students
              {missedClasses > 0 && (
                <span className="ml-2 text-red-600 font-semibold">{missedClasses} class{missedClasses !== 1 ? 'es' : ''} missed &gt;3 days</span>
              )}
            </p>
          </div>
          {tab === 'team' && (
            <button onClick={loadTeam}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:bg-gray-50">
              Refresh
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex gap-1 mb-4 bg-gray-100 rounded-xl p-1 w-fit">
          {([['team', 'Team Overview'], ['leads', 'Unassigned Leads']] as [Tab, string][]).map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              style={tab === key
                ? { background: '#2b2240', color: '#fff' }
                : { background: 'transparent', color: '#6b7280' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'team' ? (
          loading ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">Loading team data…</div>
          ) : juniors.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
              <p className="text-amber-800 font-semibold">No junior mentors assigned to you yet.</p>
              <p className="text-amber-700 text-sm mt-1">Ask the admin to assign junior mentors from the Mentors page.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {juniors.map(jm => (
                <JuniorCard key={jm.mentorId} jm={jm}
                  onReassignStudent={(s, fromMentorId) => setReassignTarget({ s, fromMentorId })} />
              ))}
            </div>
          )
        ) : (
          <UnassignedLeadsTab mentorId={mentorId} juniors={juniors} />
        )}
      </div>

      {reassignTarget && (
        <ReassignStudentModal
          student={reassignTarget.s}
          fromMentorId={reassignTarget.fromMentorId}
          juniors={juniors}
          seniorMentorId={mentorId}
          onClose={() => setReassignTarget(null)}
          onDone={() => { setReassignTarget(null); loadTeam(); }}
        />
      )}
    </TrackerLayout>
  );
};

export default SeniorTeamOverview;
